'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Compass, MoveRight } from 'lucide-react';
import { Button } from '@/core/components/ui/button';

export default function NotFoundClient() {
    const router = useRouter();
    const [countdown, setCountdown] = useState(5);

    useEffect(() => {
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    router.replace('/');
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [router]);

    return (
        <div className="relative min-h-screen flex items-center justify-center px-6 py-12 overflow-hidden bg-slate-50 dark:bg-slate-950">
            {/* Glowing background accent orbs */}
            <div className="absolute top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/10 dark:bg-primary/5 rounded-full blur-3xl pointer-events-none animate-pulse duration-3000" />
            <div className="absolute bottom-1/4 right-1/4 translate-x-1/2 translate-y-1/2 w-96 h-96 bg-indigo-500/10 dark:bg-indigo-500/5 rounded-full blur-3xl pointer-events-none animate-pulse duration-3000 delay-1000" />

            <div className="relative text-center max-w-lg w-full bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl border border-slate-200/50 dark:border-slate-800/50 rounded-3xl p-8 sm:p-12 shadow-2xl animate-fade-in">
                <div className="mx-auto w-16 h-16 bg-primary/10 dark:bg-primary/20 text-primary rounded-2xl flex items-center justify-center mb-6 animate-zoom-in">
                    <Compass className="w-8 h-8 animate-spin" style={{ animationDuration: '12s' }} />
                </div>

                <p className="text-sm font-semibold tracking-wider text-primary uppercase mb-2">Błąd 404</p>
                <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white sm:text-4xl tracking-tight mb-4">
                    Ups! Zagubiono się w sieci?
                </h1>
                
                <p className="text-base text-slate-600 dark:text-slate-400 mb-8 max-w-md mx-auto">
                    Strona, której szukasz, nie istnieje lub została przeniesiona. 
                    Za chwilę automatycznie przekierujemy Cię na stronę główną.
                </p>

                {/* Animated progress ring and countdown indicator */}
                <div className="flex flex-col items-center justify-center mb-10">
                    <div className="relative w-20 h-20 flex items-center justify-center">
                        <svg className="absolute inset-0 w-full h-full transform -rotate-90">
                            <circle
                                className="text-slate-100 dark:text-slate-800"
                                strokeWidth="4"
                                stroke="currentColor"
                                fill="transparent"
                                r="36"
                                cx="40"
                                cy="40"
                            />
                            <circle
                                className="text-primary transition-all duration-1000 ease-linear"
                                strokeWidth="4"
                                strokeDasharray="226.2"
                                strokeDashoffset={226.2 * (1 - countdown / 5)}
                                strokeLinecap="round"
                                stroke="currentColor"
                                fill="transparent"
                                r="36"
                                cx="40"
                                cy="40"
                            />
                        </svg>
                        <span className="text-2xl font-black text-slate-800 dark:text-slate-200">
                            {countdown}
                        </span>
                    </div>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-2 font-medium">
                        Przekierowanie na stronę główną...
                    </p>
                </div>

                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                        asChild
                        variant="default"
                        size="lg"
                        className="rounded-xl font-semibold shadow-sm hover:scale-[1.02] active:scale-[0.98] transition-transform"
                    >
                        <Link href="/" replace>
                            Przejdź teraz
                            <MoveRight className="w-4 h-4 ml-1" />
                        </Link>
                    </Button>
                    <Button
                        asChild
                        variant="outline"
                        size="lg"
                        className="rounded-xl font-semibold hover:scale-[1.02] active:scale-[0.98] transition-transform"
                    >
                        <Link href="/produkty" replace>
                            Zobacz produkty
                        </Link>
                    </Button>
                </div>
            </div>
        </div>
    );
}
